/**
 * Staff-Edit eines Worker-Profils "im Namen" (Slice 2 view).
 *
 * Wiederverwendung der Onboarding-Feld-Steps (WorkerProfileStep1..3), aber:
 *   - Datenquelle/Save = staff-Pfad (`workers.getById` / `workers.staffUpdate`)
 *   - alle 3 Steps gestapelt + ein "Speichern" (kein Wizard — Staff überblickt alles)
 *   - in JEDEM Status editierbar (Staff-Override; kein readOnly)
 * Audit passiert serverseitig (action `update`, payload.onBehalf).
 */
import {
  STAFF_CLEARABLE_WORKER_FIELDS,
  staffWorkerFieldsSchema,
  upsertWorkerSchema,
  type StaffWorkerFieldsInput,
  type UpsertWorkerInput,
} from '@hirebotiq-ops/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { trpc } from '../../lib/trpc.js';
import { Button } from '../ui/button.js';
import { Field } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.js';
import { Textarea } from '../ui/textarea.js';
import {
  WorkerProfileStep1,
  WorkerProfileStep2,
  WorkerProfileStep3,
} from './WorkerOnboardingForm.js';

const stripEmpty = (input: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === '' || v === undefined || v === null || (typeof v === 'number' && Number.isNaN(v))) {
      continue;
    }
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return out;
};

const isEmpty = (value: unknown): boolean =>
  value === '' ||
  value === undefined ||
  value === null ||
  (typeof value === 'number' && Number.isNaN(value)) ||
  (Array.isArray(value) && value.length === 0);

export function StaffWorkerEditForm({ workerId }: { workerId: string }) {
  const utils = trpc.useUtils();
  const q = trpc.workers.getById.useQuery({ workerId });
  const update = trpc.workers.staffUpdate.useMutation({
    onSuccess: () => {
      toast.success('Worker-Daten gespeichert (im Namen).');
      utils.workers.getById.invalidate({ workerId });
      utils.workers.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const form = useForm<UpsertWorkerInput>({
    resolver: zodResolver(upsertWorkerSchema),
    defaultValues: {},
    mode: 'onBlur',
  });
  const staffForm = useForm<StaffWorkerFieldsInput>({
    resolver: zodResolver(staffWorkerFieldsSchema),
    defaultValues: { wageCurrency: 'EUR', professionalCertifications: [] },
    mode: 'onBlur',
  });

  useEffect(() => {
    if (!q.data) return;
    const w = q.data;
    form.reset({
      firstName: w.firstName ?? undefined,
      lastName: w.lastName ?? undefined,
      maidenName: w.maidenName ?? undefined,
      dateOfBirth: w.dateOfBirth ?? undefined,
      placeOfBirth: w.placeOfBirth ?? undefined,
      addressStreet: w.addressStreet ?? undefined,
      addressZip: w.addressZip ?? undefined,
      addressCity: w.addressCity ?? undefined,
      addressCountry: w.addressCountry ?? undefined,
      phone: w.phone ?? undefined,
      nationality: w.nationality ?? undefined,
      taxId: w.taxId ?? undefined,
      taxClass: w.taxClass ?? undefined,
      religiousAffiliation: w.religiousAffiliation ?? undefined,
      childAllowances: w.childAllowances ?? undefined,
      maritalStatus: w.maritalStatus ?? undefined,
      numberOfChildren: w.numberOfChildren ?? undefined,
      bankAccountHolder: w.bankAccountHolder ?? undefined,
      iban: w.iban ?? undefined,
      bic: w.bic ?? undefined,
      socialSecurityNumber: w.socialSecurityNumber ?? undefined,
      healthInsuranceProvider: w.healthInsuranceProvider ?? undefined,
      insuranceStatus: w.insuranceStatus ?? undefined,
      residencePermitType: w.residencePermitType ?? undefined,
      residencePermitExpiry: w.residencePermitExpiry ?? undefined,
      workAuthorizationConfirmed: w.workAuthorizationConfirmed ?? undefined,
      workRestrictions: w.workRestrictions ?? undefined,
    });
    staffForm.reset({
      hourlyWage: w.hourlyWage ?? undefined,
      wageCurrency: w.wageCurrency ?? 'EUR',
      severeDisability: w.severeDisability ?? undefined,
      disabilityDegree: w.disabilityDegree ?? undefined,
      disabilityValidUntil: w.disabilityValidUntil ?? undefined,
      highestEducation: w.highestEducation ?? undefined,
      drivingLicenseClass: w.drivingLicenseClass ?? undefined,
      professionalCertifications: Array.isArray(w.professionalCertifications)
        ? w.professionalCertifications.map((item) =>
            typeof item === 'string' ? item : JSON.stringify(item),
          )
        : [],
      notes: w.notes ?? undefined,
    });
  }, [q.data, form, staffForm]);

  const onSave = async () => {
    const [profileValid, staffValid] = await Promise.all([form.trigger(), staffForm.trigger()]);
    if (!profileValid || !staffValid) {
      toast.error('Bitte die markierten Felder prüfen.');
      return;
    }
    const allValues: Record<string, unknown> = {
      ...form.getValues(),
      ...staffForm.getValues(),
    };
    const values = stripEmpty(allValues);
    const clearFields = STAFF_CLEARABLE_WORKER_FIELDS.filter(
      (field) => !isEmpty(q.data?.[field]) && isEmpty(allValues[field]),
    );
    try {
      await update.mutateAsync({ workerId, ...values, clearFields });
    } catch {
      /* Fehler wird via onError getoastet */
    }
  };

  if (q.isLoading) {
    return <p className="text-sm text-[var(--muted-foreground)]">Lade Formular …</p>;
  }

  return (
    <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Persönlich & Anschrift</h3>
        <WorkerProfileStep1 form={form} />
      </section>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Steuer & Bank</h3>
        <WorkerProfileStep2 form={form} />
      </section>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Sozialversicherung & Aufenthalt</h3>
        <WorkerProfileStep3 form={form} allowUnsetBoolean />
      </section>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Lohn</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <Field
            label="Stundenlohn"
            htmlFor="hourlyWage"
            error={staffForm.formState.errors.hourlyWage?.message}
          >
            <Input id="hourlyWage" inputMode="decimal" {...staffForm.register('hourlyWage')} />
          </Field>
          <Field
            label="Währung"
            htmlFor="wageCurrency"
            error={staffForm.formState.errors.wageCurrency?.message}
          >
            <Input id="wageCurrency" maxLength={3} {...staffForm.register('wageCurrency')} />
          </Field>
        </div>
      </section>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Schwerbehinderung & Qualifikationen</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Schwerbehinderung" htmlFor="severeDisability">
            <Select
              value={
                staffForm.watch('severeDisability') == null
                  ? 'unset'
                  : staffForm.watch('severeDisability')
                    ? 'yes'
                    : 'no'
              }
              onValueChange={(value) =>
                staffForm.setValue(
                  'severeDisability',
                  value === 'unset' ? undefined : value === 'yes',
                  { shouldValidate: true },
                )
              }
            >
              <SelectTrigger id="severeDisability">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">Nicht hinterlegt</SelectItem>
                <SelectItem value="yes">Ja</SelectItem>
                <SelectItem value="no">Nein</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field
            label="Grad der Behinderung"
            htmlFor="disabilityDegree"
            error={staffForm.formState.errors.disabilityDegree?.message}
          >
            <Input
              id="disabilityDegree"
              type="number"
              min={0}
              max={100}
              {...staffForm.register('disabilityDegree', {
                setValueAs: (value) => (value === '' ? undefined : Number(value)),
              })}
            />
          </Field>
          <Field label="Nachweis gültig bis" htmlFor="disabilityValidUntil">
            <Input
              id="disabilityValidUntil"
              type="date"
              {...staffForm.register('disabilityValidUntil')}
            />
          </Field>
          <Field label="Höchster Abschluss" htmlFor="highestEducation">
            <Input id="highestEducation" {...staffForm.register('highestEducation')} />
          </Field>
          <Field label="Führerscheinklassen" htmlFor="drivingLicenseClass">
            <Input id="drivingLicenseClass" {...staffForm.register('drivingLicenseClass')} />
          </Field>
          <Field
            label="Zertifikate"
            htmlFor="professionalCertifications"
            hint="Ein Eintrag pro Zeile"
          >
            <Textarea
              id="professionalCertifications"
              rows={4}
              value={(staffForm.watch('professionalCertifications') ?? []).join('\n')}
              onChange={(event) =>
                staffForm.setValue(
                  'professionalCertifications',
                  event.target.value
                    .split('\n')
                    .map((value) => value.trim())
                    .filter(Boolean),
                  { shouldValidate: true },
                )
              }
            />
          </Field>
        </div>
      </section>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Interne Angaben</h3>
        <Field label="Interne Notizen" htmlFor="workerNotes">
          <Textarea id="workerNotes" rows={4} {...staffForm.register('notes')} />
        </Field>
      </section>
      <div className="flex justify-end">
        <Button onClick={onSave} disabled={update.isPending}>
          {update.isPending ? 'Speichere …' : 'Speichern (im Namen)'}
        </Button>
      </div>
    </form>
  );
}
